import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse} from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { WhatsappReportRequestDto } from '../models/whatsapp-report-request-dto';
import { WhatsappDeliveryReportResponseDto } from '../models/whatsapp-delivery-report-response-dto';
import { WhatsappStepFunctionResponseDto } from '../models/whatsapp-step-function-response-dto';

@Injectable({
  providedIn: 'root',
})
export class WhatsappDeliveryReportControllerV2Service extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation fetchDeliveryReports
   */
  static readonly FetchDeliveryReportsPath = '/api/v2/delivery-report';

  /**
   * Fetch delivery reports.
   *
   * The API to fetch all the delivery reports requested for the current logged-in user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `fetchDeliveryReports()` instead.
   *
   * This method doesn't expect any request body.
   */
  fetchDeliveryReports$Response(params?: {
 }
): Observable<StrictHttpResponse<Array<WhatsappDeliveryReportResponseDto>>> {

    const rb = new RequestBuilder(this.rootUrl, WhatsappDeliveryReportControllerV2Service.FetchDeliveryReportsPath, 'get');
    if (params) {
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WhatsappDeliveryReportResponseDto>>;
      })
    );
  }

  /**
   * Fetch delivery reports.
   *
   * The API to fetch all the delivery reports requested for the current logged-in user
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `fetchDeliveryReports$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  fetchDeliveryReports(params?: {
  }
): Observable<Array<WhatsappDeliveryReportResponseDto>> {

    return this.fetchDeliveryReports$Response(params).pipe(
      map((r: StrictHttpResponse<Array<WhatsappDeliveryReportResponseDto>>) => r.body as Array<WhatsappDeliveryReportResponseDto>)
    );
  }

  /**
   * Path part for operation updateDeliveryReport
   */
  static readonly UpdateDeliveryReportPath = '/api/v2/delivery-report';

  /**
   * Update delivery report.
   *
   * The API to update the delivery report for the given delivery request ID
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateDeliveryReport()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDeliveryReport$Response(params: {
    body: WhatsappStepFunctionResponseDto
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, WhatsappDeliveryReportControllerV2Service.UpdateDeliveryReportPath, 'put');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Update delivery report.
   *
   * The API to update the delivery report for the given delivery request ID
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateDeliveryReport$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateDeliveryReport(params: {
    body: WhatsappStepFunctionResponseDto
  }
): Observable<void> {

    return this.updateDeliveryReport$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation submitDeliveryReportRequest
   */
  static readonly SubmitDeliveryReportRequestPath = '/api/v2/delivery-report';

  /**
   * Submit delivery report request.
   *
   * The API to submit a request to download delivery report for the current logged-in user
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `submitDeliveryReportRequest()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  submitDeliveryReportRequest$Response(params: {
    body: WhatsappReportRequestDto
  }
): Observable<StrictHttpResponse<Array<WhatsappDeliveryReportResponseDto>>> {

    const rb = new RequestBuilder(this.rootUrl, WhatsappDeliveryReportControllerV2Service.SubmitDeliveryReportRequestPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json'
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WhatsappDeliveryReportResponseDto>>;
      })
    );
  }

  /**
   * Submit delivery report request.
   *
   * The API to submit a request to download delivery report for the current logged-in user
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `submitDeliveryReportRequest$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  submitDeliveryReportRequest(params: {
    body: WhatsappReportRequestDto
  }
): Observable<Array<WhatsappDeliveryReportResponseDto>> {

    return this.submitDeliveryReportRequest$Response(params).pipe(
      map((r: StrictHttpResponse<Array<WhatsappDeliveryReportResponseDto>>) => r.body as Array<WhatsappDeliveryReportResponseDto>)
    );
  }

}